import getGoogleRecommendedArtworks from 'apis/google'
import { getDataLayerItemVariant } from 'lib/helpers'
import {
  convertFromCentsToDollars,
  convertFromCentsToDollarsString,
} from 'components/FormatPrice/helpers'
import { algoliaEventsApi } from 'apis/algolia'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { AddToCartGAWhitelistParams, GA4CartEventObject } from './types'
import { parseSku } from 'lib/product'

const PRODUCT_TYPES = {
  [`1`]: 'original',
  [`2`]: 'open',
  [`3`]: 'open',
  [`4`]: 'original',
}

// Carousel Api Type
export const GOOGLE = 'google'

const PLP_PARAMS = {
  event: 'category-page-view',
  pageSize: 28,
  placement: 'recently-viewed',
  experiment: '',
}

export const getCategoryForRecCarousel = (value: string): string => {
  if (value.search(/Sculpture|Collage|Installation/i) > -1) {
    return `${value}s`
  } else if (value.search(/print/i) > -1) {
    return 'Prints'
  } else if (value === 'All') {
    return 'Artworks'
  }

  return value
}

export const getGoogleArtworksPLP = async ({
  category,
  callBack,
  visitorId,
  pageCategories,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const { payload: artworks, success } = await getGoogleRecommendedArtworks({
    ...PLP_PARAMS,
    category,
    visitorId,
    pageCategories,
  })

  if (success) {
    callBack(artworks)
  }
}

export const clickArtworkCarousel = (
  ev: React.SyntheticEvent<Record<string, any>>,
  algoliaSearchIndex: string,
  algoliaUserToken: string,
  userID: number
) => {
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
  const {
    id,
    position,
    list,
    name,
    category,
    listid,
    listname,
    artid,
    brand,
    price,
  } = ev.currentTarget.dataset
  // Delete this dataLayer event in July as part of universal analytics sunset
  window.dataLayer.push({
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: {
          list, // What carousel
        },
        products: [
          {
            id,
            // Product sku
            position,
            // Location in the carousel
            name, // Artwork title
          },
        ],
      },
    },
  })
  // This is the new dataLayer event for GA4
  // Clear the previous ecommerce object.
  window.dataLayer.push({
    ecommerce: null,
  })
  window.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listid,
      item_list_name: listname,
      items: [
        {
          item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
          item_id: id || '',
          item_name: name || '',
          item_brand: brand || '',
          item_art_id: artid || '',
          item_variant: getDataLayerItemVariant(id),
          index: position,
          item_list_id: listid,
          item_list_name: listname,
          affiliation: 'Online Store',
          price: convertFromCentsToDollars(price) || 0,
          quantity: 1,
        },
      ],
    },
  })
  // Algolia event
  algoliaEventsApi({
    events: [
      {
        eventName: 'select_item',
        eventType: 'click',
        index: algoliaSearchIndex,
        userToken: algoliaUserToken,
        authenticatedUserToken: String(userID) || '',
        objectIDs: [String(artid)],
        positions: [position],
        ...(algoliaQueryIdFromLocalStorage && {
          queryID: algoliaQueryIdFromLocalStorage,
        }), // pass queryID only if its present, otherwise do not pass
      },
    ],
  })
}

export const addToCartGAWhitelist = ({
  artistName,
  category,
  price,
  sku,
  title,
  listId,
  dataPosition,
  artworkId,
}: AddToCartGAWhitelistParams): GA4CartEventObject => {
  // You can not add a PRODUCT with NO SKU so return empty object
  if (!sku) {
    return {
      event: '',
      ecommerce: null,
    }
  }

  const { productTypeId } = parseSku(sku) // [configurationId, userId, artworkId, productTypeId]

  return {
    event: `add_to_cart`,
    ecommerce: {
      currency: 'USD',
      value: convertFromCentsToDollarsString(price), // artwork price
      items: [
        {
          item_id: sku || '',
          item_name: title || '',
          affiliation: 'Online Store',
          index: dataPosition || 0,
          item_brand: artistName || '',
          item_art_id: `${artworkId}` || '',
          item_category: category || '',
          item_variant: PRODUCT_TYPES[productTypeId] || 'original',
          item_list_id: listId || '', // product list from where the product was added to cart
          price: convertFromCentsToDollarsString(price),
          quantity: 1,
        },
      ],
    },
  }
}
